import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { PayrollEmployee, PayrollMutationResult, Payslip, FieldMappingEntry, FieldMappingDoc, ExcelPreviewResult, PayrollSettings, PayslipTemplate, MonthlyPayrollInput, MonthlyInputSavePayload, MonthlyInputBulkPayload, CompensationHistoryEntry, PayrollRun, PayrollV2Status, RenderedPayslip } from '../payrollTypes';

export const usePayrollEmployees = () => {
  return useQuery({
    queryKey: ['payroll-employees'],
    queryFn: async () => {
      const response = await api.get<PayrollEmployee[]>('/api/v2/hr/payroll/employees');
      return response.data;
    }
  });
};

export const usePayslips = (month_year?: string) => {
  return useQuery({
    queryKey: ['payslips', month_year],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (month_year) params.append('month_year', month_year);
      const response = await api.get<Payslip[]>(`/api/v2/hr/payroll/payslips?${params.toString()}`);
      return response.data;
    }
  });
};

export const useGenerateBulkPayslips = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (month_year: string) => {
      const response = await api.post<PayrollMutationResult>('/api/v2/hr/payroll/generate-bulk', { month_year });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslips'] })
  });
};

/**
 * A real payslip rendered through the currently-active uploaded template
 * (api-v2/hr.js GET /payroll/render/:id) — `html: null` means no template is
 * active yet, and the caller should fall back to the generic printable layout.
 */
export const useRenderedPayslip = (id: string | null) => {
  return useQuery({
    queryKey: ['payslip-render', id],
    queryFn: async () => {
      const response = await api.get<RenderedPayslip>(`/api/v2/hr/payroll/render/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useFinalizePayslip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<PayrollMutationResult>(`/api/v2/hr/payroll/${id}/finalize`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslips'] })
  });
};

export const useUpdatePayslip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      earnings?: MonthlyInputLine[];
      deductions?: MonthlyInputLine[];
      phone?: string;
      employee_id_code?: string;
      bank_name?: string;
      bank_account?: string;
      ifsc?: string;
      doj?: string;
      designation?: string;
      department?: string;
    }) => {
      const response = await api.put<{ ok: boolean; payslip: Payslip }>(`/api/v2/hr/payroll/payslips/${id}`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      queryClient.invalidateQueries({ queryKey: ['payslip-render', variables.id] });
    }
  });
};

export const useUpdateCompensation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      user_id: string; base_salary: number; effective_from?: string;
      structure_code?: string | null; change_reason?: string;
      incentive_percent?: number | null; bonus?: number;
      advance_deduction?: number;
      phone?: string;
      mobile?: string;
      employee_id?: string;
      date_of_joining?: string | null;
      bank_name?: string;
      bank_account_no?: string;
      bank_ifsc?: string;
      exclude_from_leaderboard?: boolean;
      exclude_from_incentive?: boolean;
      custom_earnings?: MonthlyInputLine[];
      custom_deductions?: MonthlyInputLine[];
    }) => {
      const response = await api.post<PayrollMutationResult>('/api/v2/hr/payroll/compensation', data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payroll-employees'] });
      queryClient.invalidateQueries({ queryKey: ['compensation-history', variables.user_id] });
    }
  });
};

/** Full effective-dated compensation/structure-assignment history for one employee. */
export const useCompensationHistory = (userId: string | null) => {
  return useQuery({
    queryKey: ['compensation-history', userId],
    queryFn: async () => {
      const response = await api.get<CompensationHistoryEntry[]>(`/api/v2/hr/payroll/compensation/${userId}/history`);
      return response.data;
    },
    enabled: !!userId,
  });
};

// ─── Payroll Runs (maker-checker) ─────────────────────────────────────────────

export const usePayrollRuns = () => {
  return useQuery({
    queryKey: ['payroll-runs'],
    queryFn: async () => {
      const res = await api.get<PayrollRun[]>('/api/v2/hr/payroll/runs');
      return res.data;
    }
  });
};

export const useEnsurePayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (month_year: string) => {
      const res = await api.post<PayrollRun>('/api/v2/hr/payroll/runs', { month_year });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
  });
};

export const useSubmitPayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<PayrollMutationResult>(`/api/v2/hr/payroll/runs/${id}/submit`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
  });
};

export const useApprovePayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<PayrollMutationResult>(`/api/v2/hr/payroll/runs/${id}/approve`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    }
  });
};

export const useRejectPayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await api.post<PayrollMutationResult>(`/api/v2/hr/payroll/runs/${id}/reject`, { reason });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
  });
};

/** Structure-engine adoption stats — how many active employees are on a salary structure vs the legacy fallback. */
export const usePayrollV2Status = () => {
  return useQuery({
    queryKey: ['payroll-v2-status'],
    queryFn: async () => {
      const res = await api.get<PayrollV2Status>('/api/v2/hr/payroll/v2-status');
      return res.data;
    }
  });
};

// Field Mappings
export const usePayrollFieldMapping = () => {
  return useQuery({
    queryKey: ['payroll-field-mapping'],
    queryFn: async () => {
      // Server returns `{ mappings: [...] }` (api-v2/hr.js GET /payroll/field-mapping).
      const res = await api.get<FieldMappingDoc>('/api/v2/hr/payroll/field-mapping');
      return res.data;
    }
  });
};

export const useSaveFieldMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mappings: FieldMappingEntry[]) => {
      const res = await api.post('/api/v2/hr/payroll/field-mapping', { mappings });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll-field-mapping'] })
  });
};

export const usePreviewExcel = () => {
  return useMutation({
    // Path and payload match api-v2/hr.js POST /payroll/upload-excel/preview,
    // which takes the sheet plus the column mapping and answers
    // `{ preview, total_rows }`.
    mutationFn: async ({ file, mappings }: { file: File; mappings?: FieldMappingEntry[] }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (mappings) formData.append('mappings', JSON.stringify(mappings));
      const res = await api.post<ExcelPreviewResult>('/api/v2/hr/payroll/upload-excel/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    }
  });
};

export const useUploadPayrollExcel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { file: File; month_year: string; mappings?: FieldMappingEntry[] }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('month_year', data.month_year);
      if (data.mappings) formData.append('mappings', JSON.stringify(data.mappings));
      const res = await api.post<{ created: number; skipped: number; errors: string[] }>('/api/v2/hr/payroll/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslips'] })
  });
};

// Settings & Logo
export const usePayrollSettings = () => {
  return useQuery({
    queryKey: ['payroll-settings'],
    queryFn: async () => {
      const res = await api.get<PayrollSettings>('/api/v2/hr/payroll/settings');
      return res.data;
    }
  });
};

// NOTE (2026-08-16): this previously called `api.post('/api/v2/hr/payroll/settings', ...)`
// but the backend route is `PUT /payroll/settings` — a pre-existing mismatch
// that meant "Save Settings" silently 404'd. Fixed to match the real route
// while consolidating company-branding data (see Company Profile master data).
export const useSavePayrollSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<PayrollSettings>) => {
      const res = await api.put<PayrollSettings>('/api/v2/hr/payroll/settings', settings);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll-settings'] })
  });
};

// NOTE (2026-08-16): also had a path mismatch (`/payroll/logo` vs the real
// `/payroll/settings/logo`), fixed alongside the same-day discovery that the
// logo is now Company Profile's `logoUrl` (shared across letterhead/payslips/
// wherever else a logo is needed), not a payroll-only field — see
// buildEffectiveSettings() in api-v2/hr.js. Field name in the multipart body
// must be `logo` to match the backend's `uploadLogo.single('logo')`.
export const useUploadCompanyLogo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post<{ logo_url: string }>('/api/v2/hr/payroll/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-settings'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'company-profile'] });
    }
  });
};

// Templates
export const usePayslipTemplates = () => {
  return useQuery({
    queryKey: ['payslip-templates'],
    queryFn: async () => {
      const res = await api.get<PayslipTemplate[]>('/api/v2/hr/payroll/templates');
      return res.data;
    }
  });
};

export const useUploadPayslipTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name?: string; html_content?: string; file?: File; activate?: boolean }) => {
      // The route is multipart: api-v2/hr.js guards it with
      // `uploadTemplate.single('template')` and answers 400 "No template file
      // uploaded" when `req.file` is undefined. Posting this object as JSON —
      // which is what axios does with a plain object — therefore failed every
      // time, whether the admin picked a file or wrote HTML in the editor.
      const form = new FormData();

      if (data.file) {
        form.append('template', data.file, data.file.name);
      } else if (data.html_content) {
        // The editor's "save as template" path has no File to hand, so the
        // HTML is wrapped in one. The .html extension is required: the route
        // rejects anything else via `originalname.match(/\.html?$/i)`.
        const fileName = `${(data.name || 'payslip-template').replace(/[^\w-]+/g, '-')}.html`;
        form.append(
          'template',
          new Blob([data.html_content], { type: 'text/html' }),
          fileName,
        );
      } else {
        throw new Error('Provide either a template file or template HTML.');
      }

      if (data.name) form.append('name', data.name);

      // Content-Type is deliberately not set: the browser must add the
      // multipart boundary itself, and naming the type here strips it.
      const res = await api.post<PayslipTemplate>('/api/v2/hr/payroll/templates', form);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslip-templates'] })
  });
};

export const useActivatePayslipTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // PUT, not POST — api-v2/hr.js registers this as
      // `router.put('/payroll/templates/:id/activate')`. A POST fell through
      // to the SPA catch-all and resolved as an HTML 404, so activating a
      // payslip template silently never took effect.
      const res = await api.put<{ message: string }>(`/api/v2/hr/payroll/templates/${id}/activate`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslip-templates'] })
  });
};

export const useDeletePayslipTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete<{ message: string }>(`/api/v2/hr/payroll/templates/${id}`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payslip-templates'] })
  });
};

export const useTemplatePreview = () => {
  return useMutation({
    // api-v2/hr.js GET /payroll/templates/:id/preview renders the stored
    // template against a dummy payslip and answers `{ html }`.
    mutationFn: async (id: string) => {
      const res = await api.get<{ html: string }>(`/api/v2/hr/payroll/templates/${id}/preview`);
      return res.data;
    }
  });
};

// ─── Monthly Payroll Input ────────────────────────────────────────────────────

export const useMonthlyPayrollInput = (monthYear?: string) => {
  return useQuery({
    queryKey: ['monthly-payroll-input', monthYear],
    queryFn: async () => {
      const res = await api.get<MonthlyPayrollInput[]>(`/api/v2/hr/payroll/monthly-input?month_year=${monthYear}`);
      return res.data;
    },
    enabled: !!monthYear,
  });
};

export const useSaveMonthlyInput = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: MonthlyInputSavePayload) => {
      const res = await api.put<{ ok: boolean; id: string; message: string }>('/api/v2/hr/payroll/monthly-input', data);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monthly-payroll-input'] })
  });
};

export const useSaveMonthlyInputBulk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: MonthlyInputBulkPayload) => {
      const res = await api.put<{ ok: boolean; saved: number; errors: { user_id: string; error: string }[] }>(
        '/api/v2/hr/payroll/monthly-input/bulk', data
      );
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monthly-payroll-input'] })
  });
};

export const useDeleteMonthlyInput = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete<{ ok: boolean; message: string }>(`/api/v2/hr/payroll/monthly-input/${id}`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monthly-payroll-input'] })
  });
};
